import React, { useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { CustomDropdownContainer, FormFieldLabel, PageHeader } from "CommonComponent";
import { DesignationDropdown, EmployeeDropdown } from "CommonDropdowns";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  FREQUENCY_OPTIONS,
  getErrorMessage,
  runGeneration,
} from "../pafEngineApi";
import "../PafEngine.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const MONTH_OPTIONS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
].map((m) => ({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) }));

const PafEngineGenerate = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [employee, setEmployee] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [designationValue, setDesignationValue] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [month, setMonth] = useState(
    MONTH_OPTIONS.find((m) => m.value === "august") || MONTH_OPTIONS[0]
  );
  const [dryRun, setDryRun] = useState(true);
  const [force, setForce] = useState(true);
  const [report, setReport] = useState(null);

  const run = async () => {
    dispatch(showCommonLoader());
    try {
      const designationId = Array.isArray(designations) ? designations[0] : designations;
      const data = await runGeneration(
        {
          employee_id: employee?.value || undefined,
          designation_id: designationId || undefined,
          frequency: frequency?.value || undefined,
          month: month?.value || undefined,
          dry_run: dryRun,
          force,
        },
        objectViewedId
      );
      setReport(data);
      dispatch(
        notify(
          dryRun ? "Dry run completed." : "Generation completed.",
          "success"
        )
      );
    } catch (error) {
      setReport(null);
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const exportCsv = () => {
    const rows = report?.rows || [];
    const header = ["Employee", "Result", "Reason", "Appraisal Setup Name", "Policy", "Frequency"];
    const lines = [
      header.join(","),
      ...rows.map((row) =>
        [
          row.employee_name || row.employee_id || "",
          row.result || "",
          row.reason || "",
          row.setup_title || "",
          row.policy_name || "",
          row.frequency || "",
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `paf-generation-${dryRun ? "dry-run" : "live"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = report?.summary;

  return (
    <div className="paf-settings-page">
      <PageHeader title="Manual Generation" />
      <div className="main-content-div page-card">
        <Row className="g-3 mb-3">
          <Col md={4}>
            <EmployeeDropdown
              label="Employee (optional)"
              value={employee}
              onChange={setEmployee}
              isClearable
            />
          </Col>
          <Col md={4}>
            <DesignationDropdown
              selectedId={designations}
              designationValue={designationValue}
              setDesignationValue={setDesignationValue}
              designationOnChange={(selected) => {
                if (!selected) {
                  setDesignations([]);
                  setDesignationValue(null);
                  return;
                }
                const option = Array.isArray(selected) ? selected[0] : selected;
                setDesignations(option?.value ? [option.value] : []);
                setDesignationValue(option || null);
              }}
              label="Designation (optional)"
              placeholder="Select designation"
              isMulti={false}
              showSelectAllOption={false}
            />
          </Col>
          <Col md={2}>
            <FormFieldLabel>Frequency</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={FREQUENCY_OPTIONS}
              value={frequency}
              onChange={setFrequency}
              isClearable
              components={{ ValueContainer: CustomDropdownContainer }}
            />
          </Col>
          <Col md={2}>
            <FormFieldLabel>Month</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={MONTH_OPTIONS}
              value={month}
              onChange={setMonth}
              components={{ ValueContainer: CustomDropdownContainer }}
            />
          </Col>
        </Row>

        <Form.Check
          type="switch"
          id="dry-run"
          className="mb-2"
          label="Dry Run (no database writes)"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
        />
        <Form.Check
          type="switch"
          id="force"
          className="mb-3"
          label="Force (bypass calendar / auto-generate gates)"
          checked={force}
          onChange={(e) => setForce(e.target.checked)}
        />

        <Button className="n-u-btn me-2" onClick={run}>
          {dryRun ? "Run Dry Run" : "Generate Now"}
        </Button>
        {report?.rows?.length ? (
          <Button variant="outline-secondary" onClick={exportCsv}>
            Export CSV
          </Button>
        ) : null}

        {summary ? (
          <>
            <div className="paf-engine-section-title">Summary</div>
            <Row className="g-3 mb-3">
              {[
                ["Scanned", summary.employees_scanned],
                ["Eligible", summary.eligible],
                ["Skipped", summary.skipped],
                ["Generated", summary.generated],
                ["Duration ms", summary.duration_ms],
              ].map(([label, value]) => (
                <Col md={2} key={label}>
                  <div className="paf-engine-card">
                    <h6>{label}</h6>
                    <div className="metric" style={{ fontSize: 22 }}>
                      {value ?? 0}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </>
        ) : null}

        {report?.rows?.length ? (
          <>
            <div className="paf-engine-section-title">Detail</div>
            <Table responsive>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Result</th>
                  <th>Reason</th>
                  <th>Appraisal Setup Name</th>
                  <th>Policy</th>
                  <th>Frequency</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.slice(0, 100).map((row, idx) => (
                  <tr key={`${row.employee_id}-${idx}`}>
                    <td>{row.employee_name || row.employee_id}</td>
                    <td>{row.result}</td>
                    <td>{row.reason}</td>
                    <td>{row.setup_title || "—"}</td>
                    <td>{row.policy_name || "—"}</td>
                    <td>{row.frequency || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PafEngineGenerate;
